import axios from 'axios'
import {
    setAlert,
    requestStarted,
    requestFinished,
    readStarted,
    readFinished,
} from '../../features/appSlice'
import { AlertType } from '../../helpers/Constants'
import { errorMessage } from './errorMessage'
import { handleUnauthorized } from '../../helpers/session'
import store from '../../store'
import { baseURL } from './envConfig'

// Re-exported: pages import it from here (and tests mock this module).
export { errorMessage }

const axiosInstance = axios.create({
    withCredentials: true,
    baseURL,
})

/**
 * True for a request made with `background: true`: one a widget loads for
 * itself and shows its own loading state for (the Comms Sent card, report
 * generation and polling, an AI tool's run). It raises no loader at all.
 */
export const isBackground = (config) => !!config?.background

/** The request's method in lower case; axios' default is GET. */
const methodOf = (config) => (config?.method ?? 'get').toLowerCase()

/**
 * True for a request that covers the screen with the full-screen loader
 * (components/AppLoader): a write (POST, PUT, DELETE...) not marked
 * background. The overlay is what stops a second click from sending a bulk
 * send or creating a pipeline twice. Reads never block: every GET on every
 * page used to cover the screen until its answer arrived.
 *
 * @param {object} config  an axios request config
 * @returns {boolean}
 */
export const blocksScreen = (config) =>
    !isBackground(config) && methodOf(config) !== 'get'

/**
 * Which loading indicator a request feeds: 'screen' (the full-screen
 * loader, writes), 'read' (the slim bar under the header, GETs) or null
 * (background requests, which show their own state).
 *
 * @param {object} config  an axios request config
 * @returns {'screen'|'read'|null}
 */
export const loadingSlot = (config) => {
    if (isBackground(config)) return null
    return blocksScreen(config) ? 'screen' : 'read'
}

const SLOT_ACTIONS = {
    screen: [requestStarted, requestFinished],
    read: [readStarted, readFinished],
}

/**
 * Wrap a request's adapter (the network call itself) so the request holds
 * its loading slot exactly while it is on the network: taken when the
 * adapter starts, released when it settles, whether it answered, failed or
 * was cancelled in flight. A request cancelled before it was sent never
 * reaches the adapter, so it takes no slot and has none to release. (Axios
 * 0.27 throws that cancellation without a `config`, so a release driven by
 * the error's config could not tell which counter to lower.)
 *
 * @param {Function} adapter  the axios adapter the request would use
 * @param {'screen'|'read'|null} slot  from loadingSlot
 * @param {Function} dispatch  the store's dispatch
 * @returns {Function} an adapter
 */
export const withLoadingSlot = (adapter, slot, dispatch) => {
    if (!slot) return adapter
    const [start, finish] = SLOT_ACTIONS[slot]
    return (config) => {
        dispatch(start())
        let pending
        try {
            pending = Promise.resolve(adapter(config))
        } catch (err) {
            dispatch(finish())
            throw err
        }
        return pending.finally(() => dispatch(finish()))
    }
}

axiosInstance.interceptors.request.use((config) => {
    config.adapter = withLoadingSlot(
        config.adapter ?? axiosInstance.defaults.adapter,
        loadingSlot(config),
        store.dispatch
    )
    return config
})

/**
 * The confirmation text of a successful response, or '' for none.
 *
 * Most routes answer `message`; some older ones answer `Message` (pipeline
 * delete: `{success: true, Message: "Pipeline Deleted Successfully"}`), and
 * reading only `message` left those actions with no confirmation at all.
 */
export const successMessage = (data) =>
    (data && typeof data === 'object' && (data.message || data.Message)) || ''

/** True when a 200 body still reports a failure (`success: false`, `status: FAILURE`). */
const isFailureBody = (data) =>
    !!data &&
    typeof data === 'object' &&
    (data.success === false || data.status === 'FAILURE')

/**
 * Whether a 2xx response's message is worth a toast.
 *
 * Writes: yes (their confirmation). Reads: only when the body reports a
 * failure. A read's "found" message ("Template found", "Holidays Found",
 * "Pipelines found") is not news; it popped up on every page load
 * (end-to-end test, 2026-10-12). `silentToast` silences any request. HTTP
 * errors are toasted by the error handler for every method.
 */
export const shouldToast = (config, data) => {
    if (config?.silentToast) return false
    const isRead = (config?.method ?? 'get').toLowerCase() === 'get'
    return !isRead || isFailureBody(data)
}

/** What the person is told when a template was saved but the category check could not run (the AI was not available). */
export const CHECK_SKIPPED_MESSAGE =
    'Saved, but the category check could not run.'

axiosInstance.interceptors.response.use(
    (response) => {
        // A save the service made without checking the category and sub-category (the AI chosen in Settings
        // could not answer): saved, but say so, as a warning rather than a plain success.
        const checkSkipped = response?.data?.tag_check_skipped === true
        const message = checkSkipped
            ? CHECK_SKIPPED_MESSAGE
            : successMessage(response?.data)
        if (message && shouldToast(response.config, response.data))
            store.dispatch(
                setAlert({
                    open: true,
                    message,
                    type: checkSkipped
                        ? AlertType.warning
                        : response.data.success ||
                          response.data.status === 'SUCCESS'
                        ? AlertType.success
                        : AlertType.error,
                })
            )
        return response
    },
    (error) => {
        // The loading slot is already released (withLoadingSlot), for a
        // cancelled request too.
        if (axios.isCancel(error)) return Promise.reject(error)

        // admin_auth_fastapi answers 401 for a missing, expired or invalid
        // cookie: the session is over, so go to the sign-in screen. No toast,
        // the sign-in screen says what happened.
        if (error.response?.status === 401) {
            handleUnauthorized()
            return Promise.reject(error)
        }

        // `silentErrorToast`: the page shows this request's error itself (the
        // template preview dialog, the create form); a toast on top said the
        // same thing twice (end-to-end retest, 2026-10-13).
        if (!error.config?.silentErrorToast)
            store.dispatch(
                setAlert({
                    open: true,
                    message: errorMessage(error.response),
                    type: AlertType.error,
                })
            )
        return Promise.reject(error)
    }
)

export default axiosInstance
